import React, { useState, useEffect } from 'react';
import { Star, User } from 'lucide-react';
import axios from 'axios';

interface Review {
  _id: string;
  bookingId?: number;
  customer: {
    name: string;
    avatar?: string | null;
  };
  rating: number;
  comment: string;
  ratedAt: string;
  bookingDate: string;
}

interface ReviewData {
  overallRating: number;
  totalReviews: number;
  ratingDistribution: {
    5: number;
    4: number;
    3: number;
    2: number;
    1: number;
  };
  reviews: Review[];
}

interface ReviewDisplayProps {
  driverId: string;
}

const ReviewDisplay: React.FC<ReviewDisplayProps> = ({ driverId }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<ReviewData | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReviews();
  }, [driverId]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      setError('');
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:3000';
      const response = await axios.get(`${apiBaseUrl}/api/reviews/driver/${driverId}`);
      if (response.data.success) {
        setData(response.data.data);
      } else {
        setError(response.data.message || 'Failed to load reviews');
      }
    } catch (err: any) {
      console.error('Error fetching reviews:', err);
      setError(err.response?.data?.message || 'Failed to load reviews');
    } finally {
      setLoading(false);
    }
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`h-4 w-4 ${
          i < rating
            ? 'text-yellow-400 fill-current'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-black"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">{error}</p>
      </div>
    );
  }

  if (!data || data.totalReviews === 0) {
    return (
      <div className="bg-white rounded-lg p-6 text-center">
        <p className="text-gray-400">No reviews yet</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg p-6">
      {/* Overall Rating Section */}
      <div className="mb-6 pb-6 border-b border-gray-700">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-2xl font-bold text-white mb-1">
              {data.overallRating.toFixed(1)}
            </h3>
            <div className="flex items-center gap-1 mb-2">
              {renderStars(Math.round(data.overallRating))}
            </div>
            <p className="text-gray-400 text-sm">
              Based on {data.totalReviews} {data.totalReviews === 1 ? 'review' : 'reviews'}
            </p>
          </div>
        </div>

        {/* Rating Distribution */}
        <div className="space-y-2">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = data.ratingDistribution[star as keyof typeof data.ratingDistribution];
            const percentage = data.totalReviews > 0 ? (count / data.totalReviews) * 100 : 0;
            return (
              <div key={star} className="flex items-center gap-2">
                <span className="text-white text-sm w-8">{star} star</span>
                <div className="flex-1 bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-yellow-400 h-2 rounded-full"
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                <span className="text-gray-400 text-sm w-12 text-right">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        <h4 className="text-lg font-semibold text-white mb-4">
          Recent Reviews ({data.reviews.length})
        </h4>
        {data.reviews.map((review) => (
          <div
            key={review._id}
            className="bg-white rounded-lg p-4 border border-gray-700"
          >
            <div className="flex items-start gap-3 mb-3">
              <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center flex-shrink-0">
                {review.customer.avatar ? (
                  <img
                    src={review.customer.avatar}
                    alt={review.customer.name}
                    className="w-10 h-10 rounded-full object-cover"
                  />
                ) : (
                  <User className="h-5 w-5 text-white" />
                )}
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <p className="font-semibold text-white">{review.customer.name}</p>
                  <span className="text-gray-400 text-xs">
                    {new Date(review.ratedAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="flex items-center gap-1 mb-2">
                  {renderStars(review.rating)}
                </div>
                {review.comment && (
                  <p className="text-gray-300 text-sm leading-relaxed">{review.comment}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ReviewDisplay;

